import { useState } from 'react';
import { cn } from '../../../../../lib/utils';
import { primaryText, secondaryText, semiBoldText, smallTextSize } from '../../../../../styling/stylingHelper';
import {
  groupQuantityIssuesByType,
  type GroupedQuantityAuditResult,
  type GroupedIssuesResult,
  type FlaggedProductWithRestaurant,
} from '../../../../../services/admin/adminService';
import { AuditScoreReport } from '../../AuditReview';
import { FlaggedProductTable, flaggedProductKey } from './FlaggedProductTable';
import { buildQuantityBuckets } from './quantityBuckets';
import type { TFn } from '../types';

export function QuantityAuditAllView({ result, t }: { result: GroupedQuantityAuditResult; t: TFn }) {
  const [grouping, setGrouping] = useState(false);
  const [grouped, setGrouped] = useState<GroupedIssuesResult | null>(null);
  const [groupError, setGroupError] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  const onDismiss = (key: string) => setDismissed((prev) => new Set(prev).add(key));

  const allProducts: FlaggedProductWithRestaurant[] = result.platformGroups.flatMap((pg) =>
    pg.reasonGroups.flatMap((rg) => rg.products)
  );
  const remaining = allProducts.filter((p) => !dismissed.has(flaggedProductKey(p))).length;

  const handleGroupByIssueType = async () => {
    try {
      setGrouping(true);
      setGroupError(null);
      const r = await groupQuantityIssuesByType(allProducts);
      setGrouped(r);
    } catch (err: any) {
      setGroupError(err.message || t('adminTesting.issueGroups.error'));
    } finally {
      setGrouping(false);
    }
  };

  const scorePct = result.totalProductsScanned > 0
    ? ((result.totalProductsScanned - result.totalFlagged) / result.totalProductsScanned) * 100
    : 100;

  const fullBreakdown = (
    <div className="space-y-4">
      {/* Summary */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className={cn(primaryText, smallTextSize)}>
          {t('adminTesting.qtyAuditAll.summary', {
            restaurants: result.restaurantsScanned,
            scanned: result.totalProductsScanned,
          })}{' '}
          <span className={cn(semiBoldText, result.totalFlagged > 0 ? 'text-red-600 dark:text-red-400' : '')}>
            {t('adminTesting.qtyAuditAll.flagged', { count: result.totalFlagged })}
          </span>
          {dismissed.size > 0 && (
            <span className={cn(secondaryText, 'ml-2')}>
              {t('adminTesting.qtyAuditAll.remaining', { count: remaining })}
            </span>
          )}
        </p>
        <span className={cn(secondaryText, 'text-xs font-mono')}>
          {t('adminTesting.aiCost', { cost: result.aiCost.totalCostEur.toFixed(4), tokens: result.aiCost.inputTokens + result.aiCost.outputTokens })}
        </span>
      </div>

      {result.totalFlagged === 0 && (
        <div className={cn(secondaryText, smallTextSize, 'text-center py-8')}>{t('adminTesting.qtyAuditAll.empty')}</div>
      )}

      {/* Issue-type grouping (second AI pass, per supplier) */}
      {result.totalFlagged > 0 && (
        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={handleGroupByIssueType}
            disabled={grouping}
            className={cn(
              'px-3 py-1.5 rounded-lg transition-colors',
              'bg-indigo-600 dark:bg-indigo-600 hover:bg-indigo-700 dark:hover:bg-indigo-700 text-white dark:text-white',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              'text-xs', semiBoldText
            )}
          >
            {grouping ? t('adminTesting.issueGroups.running') : grouped ? t('adminTesting.issueGroups.regroup') : t('adminTesting.issueGroups.button')}
          </button>
          {grouped && (
            <span className={cn(secondaryText, 'text-xs font-mono')}>
              {t('adminTesting.issueGroups.cost', { cost: grouped.aiCost.totalCostEur.toFixed(4), tokens: grouped.aiCost.inputTokens + grouped.aiCost.outputTokens })}
            </span>
          )}
        </div>
      )}

      {groupError && (
        <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm">{groupError}</div>
      )}

      {/* AI issue-type view when grouped, else the raw reason view */}
      {grouped
        ? grouped.platformGroups.map((pg) => (
            <div key={pg.platform} className="border border-gray-200 dark:border-gray-700 rounded-lg p-3">
              <h4 className={cn(primaryText, semiBoldText, smallTextSize, 'mb-2 font-mono')}>
                {pg.platform} <span className={secondaryText}>({pg.count})</span>
              </h4>
              {pg.issueGroups.map((ig, ii) => (
                <div key={ii} className="mb-3 last:mb-0">
                  <div className={cn('text-indigo-600 dark:text-indigo-400 text-xs mb-1', semiBoldText)}>
                    {ig.issueType} <span className={secondaryText}>({ig.count})</span>
                  </div>
                  <FlaggedProductTable products={ig.products} t={t} dismissed={dismissed} onDismiss={onDismiss} />
                </div>
              ))}
            </div>
          ))
        : result.platformGroups.map((pg) => (
            <div key={pg.platform} className="border border-gray-200 dark:border-gray-700 rounded-lg p-3">
              <h4 className={cn(primaryText, semiBoldText, smallTextSize, 'mb-2 font-mono')}>
                {pg.platform} <span className={secondaryText}>({pg.count})</span>
              </h4>
              {pg.reasonGroups.map((rg, ri) => (
                <div key={ri} className="mb-3 last:mb-0">
                  <div className={cn('text-orange-600 dark:text-orange-400 text-xs mb-1')}>
                    {rg.reason} <span className={secondaryText}>({rg.count})</span>
                  </div>
                  <FlaggedProductTable products={rg.products} t={t} dismissed={dismissed} onDismiss={onDismiss} />
                </div>
              ))}
            </div>
          ))}
    </div>
  );

  return (
    <AuditScoreReport
      t={t}
      title={t('adminTesting.review.scoreTitleQtyAll')}
      scorePct={scorePct}
      scannedLabel={t('adminTesting.review.scannedProductsAll', { count: result.totalProductsScanned, restaurants: result.restaurantsScanned })}
      goodLabel={t('adminTesting.review.goodQty', { count: result.totalProductsScanned - result.totalFlagged })}
      buckets={buildQuantityBuckets(allProducts, t)}
      costLabel={t('adminTesting.aiCost', { cost: result.aiCost.totalCostEur.toFixed(4), tokens: result.aiCost.inputTokens + result.aiCost.outputTokens })}
      fullBreakdown={result.totalFlagged > 0 ? fullBreakdown : undefined}
    />
  );
}
